import React from "react";
import {
  ArrowRight,
  CakeSlice,
  Heart,
  MapPin,
  Sparkles,
  Truck,
  Users,
} from "lucide-react";

import PublicNavbar from "../components/PublicNavbar";
import "./about-page.css";

const values = [
  {
    icon: Heart,
    title: "Made with care",
    text: "Every dessert is made to order with the kind of attention you can taste in every bite.",
  },
  {
    icon: CakeSlice,
    title: "Flavor first",
    text: "Classic favorites meet creative flavors, giving every order a little personality of its own.",
  },
  {
    icon: Users,
    title: "Made for moments",
    text: "From a quiet craving to a full celebration, SugaLumps makes sweets meant to be shared and remembered.",
  },
];

export default function AboutPage() {
  return (
    <div className="sl-about-page">
      <PublicNavbar />

      <main>
        <section className="sl-about-hero">
          <div className="sl-about-shell sl-about-hero-grid">
            <div className="sl-about-hero-copy">
              <span className="sl-about-eyebrow">OUR STORY</span>

              <h1>
                Sweet treats made
                <span> from the heart.</span>
              </h1>

              <p>
                SugaLumps Cupcakery &amp; Sweet Treats is a Black woman-owned
                bakery creating handcrafted desserts for celebrations,
                cravings, and all the little moments worth making sweeter.
              </p>

              <div className="sl-about-hero-actions">
                <a href="/menu" className="sl-about-primary-cta">
                  Explore the Menu
                  <ArrowRight size={16} strokeWidth={1.8} />
                </a>

                <a href="/contact" className="sl-about-secondary-cta">
                  Contact Us
                </a>
              </div>

              <div className="sl-about-quick-facts">
                <div>
                  <Truck size={17} strokeWidth={1.6} />
                  <span>
                    <strong>Delivery only</strong>
                    <small>Serving the DMV</small>
                  </span>
                </div>

                <div>
                  <MapPin size={17} strokeWidth={1.6} />
                  <span>
                    <strong>Silver Spring, Maryland</strong>
                    <small>Made locally</small>
                  </span>
                </div>

                <div>
                  <Sparkles size={17} strokeWidth={1.6} />
                  <span>
                    <strong>Made to order</strong>
                    <small>Fresh for every customer</small>
                  </span>
                </div>
              </div>
            </div>

            <div className="sl-about-hero-visual">
              <div className="sl-about-hero-photo-wrap">
                <img
                  src="/img/owner1.jpeg"
                  alt="Owner of SugaLumps Cupcakery & Sweet Treats"
                  className="sl-about-owner-photo"
                />

                <div className="sl-about-photo-label">
                  <span>MEET THE OWNER</span>
                  <strong>The heart behind SugaLumps</strong>
                </div>
              </div>

              <div className="sl-about-floating-card">
                <Heart size={22} strokeWidth={1.5} />
                <div>
                  <small>HANDCRAFTED WITH LOVE</small>
                  <strong>Every order is personal.</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="sl-about-story">
          <div className="sl-about-shell sl-about-story-grid">
            <div className="sl-about-story-photo">
              <img
                src="/img/dessert.jpeg"
                alt="Handcrafted SugaLumps desserts"
              />

              <div className="sl-about-story-photo-badge">
                <Sparkles size={18} strokeWidth={1.6} />
                <span>
                  <small>THE SUGALUMPS WAY</small>
                  <strong>Fresh. Personal. Memorable.</strong>
                </span>
              </div>
            </div>

            <div className="sl-about-story-copy">
              <span className="sl-about-eyebrow">HOW IT STARTED</span>

              <h2>A love for baking turned into something worth sharing.</h2>

              <p>
                SugaLumps was built around one simple idea: dessert should feel
                special before you even take the first bite. Every order is
                prepared with care, whether it is a box of cupcakes for a
                family gathering, a cheesecake for someone you love, or a
                custom assortment for a celebration.
              </p>

              <p>
                The menu blends comforting classics with bold, creative
                flavors. Customers can choose from cupcakes, pie pockets,
                dessert lumpia, pies, cheesecakes, cakes, and dessert boxes —
                with mix-and-match options available for select packs.
              </p>

              <div className="sl-about-owner-note">
                <span className="sl-about-owner-note-line" />

                <div>
                  <strong>More than dessert.</strong>
                  <p>
                    SugaLumps is about creating something people remember long
                    after the last bite.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="sl-about-values">
          <div className="sl-about-shell">
            <div className="sl-about-section-heading">
              <div>
                <span className="sl-about-eyebrow">WHAT MATTERS TO US</span>
                <h2>Made differently, on purpose.</h2>
              </div>

              <p>
                Thoughtful ingredients, creative flavors, and personal care are
                at the center of every SugaLumps order.
              </p>
            </div>

            <div className="sl-about-values-grid">
              {values.map(({ icon: Icon, title, text }) => (
                <article className="sl-about-value-card" key={title}>
                  <span className="sl-about-value-icon">
                    <Icon size={24} strokeWidth={1.5} />
                  </span>

                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="sl-about-owner-section">
          <div className="sl-about-shell sl-about-owner-grid">
            <div className="sl-about-owner-card">
              <div className="sl-about-owner-image-wrap">
                <img
                  src="/img/owner2.jpeg"
                  alt="Owner and baker of SugaLumps"
                />
              </div>

              <div className="sl-about-owner-card-caption">
                <span>OWNER &amp; BAKER</span>
                <strong>The heart behind SugaLumps</strong>
              </div>
            </div>

            <div className="sl-about-owner-copy">
              <span className="sl-about-eyebrow">MEET THE OWNER</span>

              <h2>The hands and heart behind every sweet.</h2>

              <p>
                This section is ready for the owner's real story and photo.
                For now, the layout uses a temporary image so you can see how
                the finished page will feel.
              </p>

              <p>
                You can use this space later for her personal introduction:
                what inspired SugaLumps, how she started baking, what she loves
                most about creating for customers, and what she hopes people
                feel when they receive an order.
              </p>

              <blockquote className="sl-about-owner-quote">
                “Desserts made with care, created for the moments you want to
                remember.”
              </blockquote>
            </div>
          </div>
        </section>

        <section className="sl-about-cta-section">
          <div className="sl-about-shell">
            <div className="sl-about-cta-card">
              <div className="sl-about-cta-image">
                <img
                  src="/img/image0.jpeg"
                  alt="SugaLumps handcrafted desserts"
                />
              </div>

              <div className="sl-about-cta-copy">
                <span className="sl-about-eyebrow">READY FOR SOMETHING SWEET?</span>
                <h2>Find your new favorite.</h2>
                <p>
                  Browse the menu, build your flavor combinations, and let
                  SugaLumps make your next moment a little sweeter.
                </p>
              </div>

              <a href="/menu" className="sl-about-cta-button">
                View the Menu
                <ArrowRight size={16} strokeWidth={1.8} />
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}